import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import clsx from 'clsx';
import React, {
    useCallback,
    useEffect,
    useMemo,
} from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { dashboardLicenses } from '../../common/constants/Constant';
import { TabTelemetryMap } from '../../common/constants/TelemetryEvents';
import useCheckLicense from '../../common/hooks/useCheckLicense';
import { useTrialServiceTab } from '../../services/licensing/TrialPerSku';
import {
    EnableUserLicensingSelector,
    isUnlicensedSelector,
} from '../../store/selectors';
import { useTelemetryHelper } from '../../telemetry/TelemetryHelper';
import { ConsumablePanel } from '../licensing/ConsumablePanel';
import { useCreditsExperience } from '../licensing/consumablesTab/useCreditsExperience';
import { ConsumablesV2 } from '../licensing/consumablesTab/v2/ConsumablesV2';
import { LicensesSettingsPanel } from '../licensing/LicensesSettingsPanel';
import { TrialPerSkuPanel } from '../licensing/trialPerSku/TrialPerSkuPanel';
import { Unlicensed } from '../licensing/Unlicensed';
import { useHasAnyLicensingPermission } from '../licensing/useCanManageLicensingData';
import { ViewLicensesPanel } from '../licensing/ViewLicensesPanel';

const useStyles = makeStyles(theme =>
    createStyles({
        content: { marginTop: '8px' },
        tabIndicator: { height: '3px' },
        licenseLabel: { marginTop: '24px' },
        unlicensed: { marginTop: '32px' },
        newUiTabs: { marginBottom: '10px' },
        allocatedLabel: {
            margin: '12px 0',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
    }),
);

const TAB_QUERY_PARAM = 'tab';
const VALID_TABS = new Set([
    dashboardLicenses.USERS,
    dashboardLicenses.ROBOTS_AND_SERVICES,
    dashboardLicenses.CONSUMABLES,
    dashboardLicenses.TRIAL_SERVICES,
    dashboardLicenses.SETTINGS,
]);

const UsageChartsComponent: React.FC<{
    isServiceMode: boolean;
    tenantIds?: string[];
    tenantName?: string;
}> = ({
    tenantIds, isServiceMode, tenantName,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const [ searchParams, setSearchParams ] = useSearchParams();

    const EnableUserLicensing = useSelector(EnableUserLicensingSelector);
    const isUnlicensedMode = useSelector(isUnlicensedSelector);
    const enableConsumablesTab = useFeatureFlagValue(Features.EnableConsumablesTab.name);
    const enableConsumablesV2 = useFeatureFlagValue(Features.EnableConsumablesV2Experience.name);
    const isFLTEnabled = useFeatureFlagValue(Features.EnableFLTExperience.name);
    const hasAnyLicensingPermission = useHasAnyLicensingPermission();
    const { isCommunity } = useCheckLicense();
    const { plan: creditsPlan } = useCreditsExperience();
    const {
        shouldSeeTrialServicesTab, trialServicesResponse,
    } = useTrialServiceTab();

    const { logEvent } = useTelemetryHelper();

    const shouldShowTabs = useMemo(
        () => !EnableUserLicensing || !isServiceMode || enableConsumablesTab,
        [ EnableUserLicensing, isServiceMode, enableConsumablesTab ]);

    const shouldShowUsersTab = useMemo(
        () => !EnableUserLicensing || !isServiceMode,
        [ EnableUserLicensing, isServiceMode ]);

    const shouldShowConsumablesTab = useMemo(
        () => enableConsumablesTab && isServiceMode && !creditsPlan,
        [ enableConsumablesTab, isServiceMode, creditsPlan ]);

    const shouldShowTrialServicesTab = useMemo(
        () => !isServiceMode && shouldSeeTrialServicesTab,
        [ isServiceMode, shouldSeeTrialServicesTab ]);

    const settingsTenantId = tenantIds?.length === 1 ? tenantIds[0] : '';
    const shouldShowSettingsTab = useMemo(
        () => isServiceMode && isFLTEnabled && !!settingsTenantId && hasAnyLicensingPermission,
        [ isServiceMode, isFLTEnabled, settingsTenantId, hasAnyLicensingPermission ]);

    const defaultTab = useMemo(
        () => (EnableUserLicensing && !isServiceMode
            ? dashboardLicenses.USERS
            : (EnableUserLicensing ? dashboardLicenses.ROBOTS_AND_SERVICES : dashboardLicenses.USERS)),
        [ EnableUserLicensing, isServiceMode ]
    );

    const initialTab = useMemo(() => {
        const tabParam = searchParams.get(TAB_QUERY_PARAM);

        if (!tabParam || !VALID_TABS.has(tabParam)) {
            return defaultTab;
        }
        if (tabParam === dashboardLicenses.USERS && !shouldShowUsersTab) {
            return defaultTab;
        }
        if (tabParam === dashboardLicenses.CONSUMABLES && !shouldShowConsumablesTab) {
            return defaultTab;
        }
        if (tabParam === dashboardLicenses.TRIAL_SERVICES && !shouldShowTrialServicesTab) {
            return defaultTab;
        }
        if (tabParam === dashboardLicenses.SETTINGS && !shouldShowSettingsTab) {
            return defaultTab;
        }

        return tabParam;
    }, [
        searchParams,
        defaultTab,
        shouldShowUsersTab,
        shouldShowConsumablesTab,
        shouldShowTrialServicesTab,
        shouldShowSettingsTab,
    ]);

    const [ value, setValue ] = React.useState(initialTab);

    useEffect(() => {
        if (searchParams.has(TAB_QUERY_PARAM)) {
            setValue(initialTab);
        }
    }, [ initialTab, searchParams ]);

    const handleChange = useCallback(
        (_: any, newValue: string) => {
            setValue(newValue);
            setSearchParams(prev => {
                prev.set(TAB_QUERY_PARAM, newValue);
                return prev;
            });
            const telemetryEventName = TabTelemetryMap.get(newValue);
            if (telemetryEventName) {
                if (tenantIds?.length === 1) {
                    logEvent(telemetryEventName, { CloudTenantId: tenantIds[0] });
                } else {
                    logEvent(telemetryEventName);
                }
            }
        },
        [ setSearchParams, logEvent, tenantIds ],
    );

    if (isUnlicensedMode) {
        return (
            <div className={classes.unlicensed}>
                <Unlicensed />
            </div>
        );
    }

    return (
        <div>
            <div>
                {shouldShowTabs && (
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="primary"
                        textColor="primary"
                        className={clsx('default', classes.newUiTabs)}
                    >
                        {shouldShowUsersTab && <Tab
                            value={dashboardLicenses.USERS}
                            label={translate({ id: 'CLIENT_USERS' })}
                            className="default"
                            data-cy="usage-chart-users-tab"
                            disableRipple
                        />}
                        <Tab
                            value={dashboardLicenses.ROBOTS_AND_SERVICES}
                            label={translate({ id: 'CLIENT_ROBOTS_AND_SERVICES' })}
                            className="default"
                            data-cy="usage-chart-robots-and-services-tab"
                            disableRipple
                        />
                        { shouldShowConsumablesTab && <Tab
                            value={dashboardLicenses.CONSUMABLES}
                            label={translate({ id: 'CONSUMABLES' })}
                            className="default"
                            data-cy="consumables-tab"
                            disableRipple
                        />}
                        {shouldShowTrialServicesTab && (
                            <Tab
                                value={dashboardLicenses.TRIAL_SERVICES}
                                label={translate({ id: 'CLIENT_FREE_TRIALS_SERVICES' })}
                                className="default"
                                data-cy="trial-services-tab"
                                disableRipple
                            />
                        )}
                        {shouldShowSettingsTab && (
                            <Tab
                                value={dashboardLicenses.SETTINGS}
                                label={translate({ id: 'CLIENT_SETTINGS' })}
                                className="default"
                                data-cy="settings-tab"
                                disableRipple
                            />
                        )}
                    </Tabs>
                )}
                {value === dashboardLicenses.TRIAL_SERVICES ? (
                    <TrialPerSkuPanel
                        trialServices={trialServicesResponse}
                        tenantIds={tenantIds}
                        isHome />
                ) : (value === dashboardLicenses.SETTINGS && shouldShowSettingsTab ? (
                    <LicensesSettingsPanel tenantId={settingsTenantId} />
                ) : (value === dashboardLicenses.CONSUMABLES && shouldShowConsumablesTab ? (
                    enableConsumablesV2 && !isCommunity ? (
                        <ConsumablesV2
                            tenantId={tenantIds?.length === 1 ? tenantIds[0] : undefined}
                            tenantName={tenantName} />
                    ) : (
                        <ConsumablePanel
                            tenantId={tenantIds?.length === 1 ? tenantIds[0] : undefined}
                            isServiceMode />
                    )
                ) : (
                    <ViewLicensesPanel
                        selectedTab={value}
                        // Deliberately the raw flag, not shouldShowConsumablesTab. This prop tells the
                        // panel to leave consumable products out of Robots & Services, and that stays
                        // true for a Community organization even though its Consumables tab is gone:
                        // their consumption story is the organization-level Credits view, so those rows
                        // are not moved back here.
                        enabledConsumablesTab={enableConsumablesTab}
                        isHome
                        useLegacyProducts={!EnableUserLicensing && value === dashboardLicenses.USERS}
                        isServiceMode={isServiceMode}
                        tenantId={tenantIds?.length === 1 ? tenantIds[0] : ''}
                        tenantIds={tenantIds}
                    />)
                ))}

            </div>
        </div>
    );
};

export default UsageChartsComponent;
